import { useCheckPasswordStrength, useRegister } from '@hooks/useAuth';
import { getErrorMessage, getErrorTitle } from '@utils/errorMessages';
import { AlertCircle, Check, Eye, EyeOff, Info, Loader2, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PasswordStrengthIndicator } from '@/components/PasswordStrengthIndicator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export function SignupForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [nameValue, setNameValue] = useState<string>('');
  const [passwordValue, setPasswordValue] = useState<string>('');
  const [confirmPasswordValue, setConfirmPasswordValue] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const registerMutation = useRegister();
  const {
    strength: passwordStrength,
    isChecking: isCheckingStrength,
    error: strengthError,
    checkStrength,
    meetsRequirements,
  } = useCheckPasswordStrength();

  const nameInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const confirmPasswordInputRef = useRef<HTMLInputElement>(null);

  // Get invitation params from URL
  const invitationId = searchParams.get('invitation');
  const organizationId = searchParams.get('organization');

  // Redirect to login if no invitation params
  useEffect(() => {
    if (!invitationId || !organizationId) {
      navigate('/login', { replace: true });
    }
  }, [invitationId, organizationId, navigate]);

  const validatePasswords = (): string | null => {
    if (passwordValue !== confirmPasswordValue) {
      return 'Password and confirm password do not match';
    }
    if (!meetsRequirements) {
      return 'Password does not meet security requirements';
    }
    return null;
  };

  const isFormValid = (): boolean => {
    return (
      nameValue.trim().length >= 2 &&
      passwordValue === confirmPasswordValue &&
      meetsRequirements &&
      !isCheckingStrength
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate name
    if (nameValue.trim().length < 2) {
      setErrorMessage('Name must be at least 2 characters');
      return;
    }

    // Validate passwords
    const passwordError = validatePasswords();
    if (passwordError) {
      setErrorMessage(passwordError);
      return;
    }

    if (!invitationId || !organizationId) {
      setErrorMessage('Invalid invitation link');
      return;
    }

    setErrorMessage('');

    registerMutation.mutate(
      {
        invitation_id: invitationId,
        organization_id: organizationId,
        name: nameValue.trim(),
        password: passwordValue.trim(),
        confirm_password: confirmPasswordValue.trim(),
      },
      {
        onError: (err: any) => {
          const apiMessage = err?.response?.data?.message || err?.response?.data?.error;
          setErrorMessage(
            apiMessage || err.message || 'Failed to complete registration. Please try again.',
          );
        },
      },
    );
  };

  // Don't render if no invitation params
  if (!invitationId || !organizationId) {
    return null;
  }

  return (
    <div className="w-full max-w-md space-y-8">
      <div className="space-y-2">
        <h2 className="text-3xl font-semibold">Complete Registration</h2>
        <p className="text-muted-foreground">Set up your account to join the organization.</p>
      </div>

      {/* Info banner */}
      <div className="bg-primary/10 border border-primary/50 text-primary px-4 py-3 rounded-md text-sm">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium">You&apos;ve been invited</p>
            <p className="mt-1">Please complete your registration to access the platform.</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Field */}
        <div className="space-y-2">
          <label htmlFor="name" className="text-sm font-medium">
            Full Name
          </label>
          <div className="relative">
            <Input
              id="name"
              ref={nameInputRef}
              value={nameValue}
              placeholder="Enter your full name"
              type="text"
              autoComplete="name"
              className={`w-full h-12 px-4 pr-10 ${registerMutation.isError || errorMessage ? 'border-destructive focus-visible:ring-destructive' : ''}`}
              onChange={e => {
                setNameValue(e.target.value);
                setErrorMessage('');
                registerMutation.reset();
              }}
            />
            {nameValue && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {nameValue.trim().length >= 2 ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <X className="h-4 w-4 text-destructive" />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Password Field */}
        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-medium">
            Password
          </label>
          <div className="relative">
            <Input
              id="password"
              ref={passwordInputRef}
              value={passwordValue}
              placeholder="Create a strong password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className={`w-full h-12 px-4 pr-10 ${registerMutation.isError || errorMessage ? 'border-destructive focus-visible:ring-destructive' : ''}`}
              onChange={e => {
                setPasswordValue(e.target.value);
                setErrorMessage('');
                registerMutation.reset();
                // Check password strength as user types
                checkStrength(e.target.value, [nameValue]);
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          {/* Password Strength Indicator */}
          <PasswordStrengthIndicator
            strength={passwordStrength}
            isChecking={isCheckingStrength}
            error={strengthError}
          />
        </div>

        {/* Confirm Password Field */}
        <div className="space-y-2">
          <label htmlFor="confirmPassword" className="text-sm font-medium">
            Confirm Password
          </label>
          <div className="relative">
            <Input
              id="confirmPassword"
              ref={confirmPasswordInputRef}
              value={confirmPasswordValue}
              placeholder="Confirm your password"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className={`w-full h-12 px-4 pr-10 ${registerMutation.isError || errorMessage ? 'border-destructive focus-visible:ring-destructive' : ''}`}
              onChange={e => {
                setConfirmPasswordValue(e.target.value);
                setErrorMessage('');
                registerMutation.reset();
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          {confirmPasswordValue && passwordValue && confirmPasswordValue !== passwordValue && (
            <p className="text-xs text-destructive">Passwords do not match</p>
          )}
          {confirmPasswordValue &&
            passwordValue &&
            confirmPasswordValue === passwordValue &&
            meetsRequirements && (
              <p className="text-xs text-emerald-600 flex items-center gap-1">
                <Check className="h-3 w-3" />
                Passwords match
              </p>
            )}
        </div>

        {/* Error message display */}
        {(registerMutation.isError || errorMessage) && (
          <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium">
                  {registerMutation.error
                    ? getErrorTitle(registerMutation.error as any, 'Registration Failed')
                    : 'Registration Failed'}
                </p>
                <p className="mt-1">
                  {errorMessage ||
                    (registerMutation.error ? getErrorMessage(registerMutation.error as any) : '')}
                </p>
              </div>
            </div>
          </div>
        )}

        <Button
          type="submit"
          className="w-full h-12"
          disabled={!isFormValid() || registerMutation.isPending}
        >
          {registerMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating Account...
            </>
          ) : (
            'Complete Registration'
          )}
        </Button>

        <div className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Button
            type="button"
            variant="link"
            className="p-0 h-auto"
            onClick={() => navigate('/login')}
          >
            Sign in
          </Button>
        </div>
      </form>
    </div>
  );
}
